import React, {useState} from 'react';

import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Row from 'ui-library/bs/Row';
import {ButtonStyle} from 'ui-library/buttons/Button';
import useFormInput from 'ui-library/hooks/useFormInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import NumericInput from 'ui-library/inputs/NumericInput';
import TextField from 'ui-library/inputs/TextField';
import Modal from 'ui-library/modals/Modal';

import {
  MAX_LAYOUT_COLUMNS,
  MAX_LAYOUT_ROWS,
  MIN_LAYOUT_COLUMNS,
  MIN_LAYOUT_ROWS,
} from '../utils/LayoutConstants';

export interface LayoutSettingsModalResult {
  name: string;
  rows: number;
  columns: number;
}

export interface LayoutSettingsModalProps {
  currentName: string;
  currentRows: number;
  currentColumns: number;
  onRequestClose: () => void;
  onRequestSubmit: (result: LayoutSettingsModalResult) => void;
}

export default function LayoutSettingsModal({
  currentName,
  currentRows,
  currentColumns,
  onRequestClose,
  onRequestSubmit,
}: LayoutSettingsModalProps): JSX.Element {
  const nameFormInput = useFormInput(currentName);
  const [rows, setRows] = useState<number | null>(currentRows);
  const [columns, setColumns] = useState<number | null>(currentColumns);

  const isNameValid = nameFormInput.value.trim().length > 0;
  const isRowsValid = rows != null && rows >= MIN_LAYOUT_ROWS && rows <= MAX_LAYOUT_ROWS;
  const isColumnsValid = columns != null && columns >= MIN_LAYOUT_COLUMNS && columns <= MAX_LAYOUT_COLUMNS;
  const isValid = isNameValid && isRowsValid && isColumnsValid;

  function handleSubmit(): void {
    if (!isValid || rows == null || columns == null) {
      return;
    }
    onRequestSubmit({
      name: nameFormInput.value.trim(),
      rows,
      columns,
    });
  }

  return (
    <Modal
      buttons={[{style: ButtonStyle.SECONDARY, submit: true, text: 'Save', disabled: !isValid}]}
      onRequestClose={onRequestClose}
      onRequestSubmit={handleSubmit}
      title="Layout settings"
    >
      <Row>
        <Col sm={12}>
          <FormGroup>
            <TextField
              label="Name"
              dataTestAttrs={{component: 'LayoutSettingsModal', element: 'name'}}
              {...nameFormInput}
              hasError={!isNameValid}
            />
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col sm={6}>
          <FormGroup>
            <FieldLabel label="Rows" />
            <NumericInput
              dataTestAttrs={{component: 'LayoutSettingsModal', element: 'rows'}}
              isDisabled={false}
              required={true}
              validateInteger={true}
              value={rows}
              min={MIN_LAYOUT_ROWS}
              max={MAX_LAYOUT_ROWS}
              onChange={(e) => {
                setRows(e.target.numericValue);
              }}
            />
          </FormGroup>
        </Col>
        <Col sm={6}>
          <FormGroup>
            <FieldLabel label="Columns" />
            <NumericInput
              dataTestAttrs={{component: 'LayoutSettingsModal', element: 'columns'}}
              isDisabled={false}
              required={true}
              validateInteger={true}
              value={columns}
              min={MIN_LAYOUT_COLUMNS}
              max={MAX_LAYOUT_COLUMNS}
              onChange={(e) => {
                setColumns(e.target.numericValue);
              }}
            />
          </FormGroup>
        </Col>
      </Row>
    </Modal>
  );
}
